"use strict";
const _ = require('lodash');
const { orderUnits, alcoholTaxClasses } = require('./article.const');
const productCategoryTree = require('./product-category-tree');
const literToAppropriateUnit = require('./volume');
const { getTranslation } = require('../translate');
const { invertedOriginIsoCodesWithThreeCharacters, invertedOriginIsoCodesWithTwoCharacters, } = require('./origin-iso-codes');
const { warehouseStatus, deliveryStatus, calculateDisposableSalesUnits, } = require('./warehouseStatus');
const organization = require('../organization');
const DEFAULT_BATCH_NUMBER = 'Ospecificerad';
// helper functions to check properties of the entity goes here
function calculateRequiresAlcoholPermit(productCategory, alcoholPercentage) {
    if (_.startsWith(productCategory, 'Dryck|Öl') && alcoholPercentage <= 3.5) {
        return false;
    }
    if (_.startsWith(productCategory, 'Dryck|Cider & Ready to drink') &&
        alcoholPercentage <= 2.25) {
        return false;
    }
    if (alcoholPercentage > 1.2) {
        return true;
    }
    return false;
}
function isValidGTIN(GTIN) {
    if (!GTIN)
        return false;
    if (!GTIN.match(/^[0-9]*$/g))
        return false;
    const lastNumber = GTIN.slice(-1);
    const multipliers = [3, 1];
    let sum = 0;
    for (let i = 0; i < GTIN.length - 1; i += 1) {
        sum += Number(GTIN[i]) * multipliers[i % 2];
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit === Number(lastNumber);
}
function requiresEMCS(article) {
    if (article.alcoholTaxPaid) {
        return false;
    }
    switch (article.alcoholTaxClass) {
        case 'Alcoholfree':
            return false;
        case 'Beer':
            return article.alcoholPercentage > 2.8;
        case 'Wine':
            return article.alcoholPercentage > 2.25;
        case 'MidRange':
            return article.alcoholPercentage > 1.2;
        case 'Spirits':
            return article.alcoholPercentage > 1.2;
        default:
            return false;
    }
}
function getMinOrderableQuantityIncrement(article) {
    if (!article)
        return 1;
    if (article.orderUnit === 'single')
        return 1;
    if (article.orderUnit === 'weight')
        return article.minWeightIncrement;
    return article.colliSize;
}
function translateToOldSalesType(article) {
    if (_.includes(organization.FLAIVY_ORGANIZATION_IDS, article.owningSupplierOrganizationId)) {
        return 'OwnedByDrycks';
    }
    if (article.onCopack) {
        return 'Commission';
    }
    return 'DropShipping';
}
function calculateBookKeepingAccounts(existing, productCategory, alcoholPercentage) {
    // TODO this needs to be changed based on our own bookkeeping - and probably should move to our fortnox integration
    const currentSalesType = translateToOldSalesType(existing);
    if (_.startsWith(productCategory, 'Dryck|Öl') && alcoholPercentage <= 3.5) {
        return productCategoryTree.getInheritedProperty(productCategory, `alcoholFreeBookKeepingAccounts.${currentSalesType}`);
    }
    if (_.startsWith(productCategory, 'Dryck|Cider & Ready to drink') &&
        alcoholPercentage <= 2.25) {
        return productCategoryTree.getInheritedProperty(productCategory, `alcoholFreeBookKeepingAccounts.${currentSalesType}`);
    }
    if (alcoholPercentage > 0.5) {
        return productCategoryTree.getInheritedPropertyWithFallback(productCategory, `alcoholBookKeepingAccounts.${currentSalesType}`, `alcoholFreeBookKeepingAccounts.${currentSalesType}`);
    }
    return productCategoryTree.getInheritedPropertyWithFallback(productCategory, `alcoholFreeBookKeepingAccounts.${currentSalesType}`, `alcoholBookKeepingAccounts.${currentSalesType}`);
}
function calculateVAT(productCategory, alcoholPercentage) {
    if (_.startsWith(productCategory, 'Dryck|Öl') && alcoholPercentage <= 3.5) {
        return 12;
    }
    if (_.startsWith(productCategory, 'Dryck') && alcoholPercentage <= 2.25) {
        return 12;
    }
    return productCategoryTree.getInheritedProperty(productCategory, 'VAT') || 25;
}
function getDisposableQuantity(stockInfo, orderState) {
    if (_.includes(['draft', 'created'], orderState)) {
        return _.round(stockInfo.quantityInStock - stockInfo.reservedQuantity, 3);
    }
    if (orderState === 'waitingForRestock') {
        return _.round(stockInfo.quantityInStock -
            stockInfo.reservedQuantityProcessedByWarehouse, 3);
    }
    if (_.includes(['received', 'waitingForAdvancePayment'], orderState)) {
        return _.round(stockInfo.quantityInStock - stockInfo.reservedQuantityApproved, 3);
    }
    if (_.includes(['approved'], orderState)) {
        return _.round(stockInfo.quantityInStock -
            stockInfo.reservedQuantityProcessedByWarehouse, 3);
    }
    if (orderState === 'inWarehouseProcess') {
        return _.round(stockInfo.quantityInStock, 3);
    }
    return _.round(stockInfo.quantityInStock - stockInfo.reservedQuantity, 3);
}
function calculateAlcoholTaxClass(params, productCategory, alcoholPercentage) {
    if (alcoholPercentage === 0) {
        return 'Alcoholfree';
    }
    if (alcoholPercentage > 22) {
        return alcoholTaxClasses.Spirits;
    }
    if (alcoholPercentage > 18 && _.includes(productCategory, 'Vin')) {
        return alcoholTaxClasses.MidRange;
    }
    const potentialAlcTaxClasses = productCategoryTree.getInheritedProperty(productCategory, 'alcoholTaxClasses');
    if (potentialAlcTaxClasses.length === 1) {
        return potentialAlcTaxClasses[0];
    }
    if (params.alcoholTaxClass) {
        return params.alcoholTaxClass;
    }
    return null;
}
function calculateProductExciseCode(alcoholTaxClass) {
    if (alcoholTaxClass === 'Beer') {
        return 'B000';
    }
    if (alcoholTaxClass === 'MidRange') {
        return 'I000';
    }
    return null;
}
function calculateName(volume, container, articleBrand, shortName) {
    if (!shortName)
        return null;
    const translatedContainer = container
        ? getTranslation('article.containers', container)
        : '';
    const literValue = literToAppropriateUnit(volume);
    let containerDescription = '';
    let brandName = '';
    if (volume) {
        containerDescription = `(${translatedContainer} ${literValue})`;
    }
    if (articleBrand) {
        brandName = _.truncate(articleBrand.name, {
            length: 15,
            separator: ' ',
            omission: '',
        });
    }
    const truncatedShortName = _.truncate(shortName, {
        length: 50 - 4 - brandName.length - containerDescription.length,
        separator: ' ',
        omission: '',
    });
    if (brandName) {
        return _.trim(`${brandName} - ${truncatedShortName} ${containerDescription}`);
    }
    return _.trim(`${truncatedShortName} ${containerDescription}`);
}
function calculateNetWeightInKg(article) {
    return article.volume;
}
const isFlaivyAssetImage = image => _.includes(image, 'assets.flaivy.com');
function calculateGrossWeightInKg(article) {
    if (!article)
        return 0;
    if (article.weight)
        return article.weight / 1000;
    // These are rough approximations but should be an improvement over just guessing.
    const containerWeightByVolumeCoefficient = {
        Bottle: 1.4,
        Can: 1.04,
        Keg: 1.2,
        Tetra: 1.05,
        Pet: 1.02,
        Paper: 1.01,
        BIB: 1.01,
        Bag: 1.01,
        Other: 1.01,
    };
    if (!containerWeightByVolumeCoefficient[article.container]) {
        return calculateNetWeightInKg(article);
    }
    return (calculateNetWeightInKg(article) *
        containerWeightByVolumeCoefficient[article.container]);
}
function getImmutablefields(productCategory) {
    const immutableFields = productCategoryTree.getInheritedProperty(productCategory, 'immutableFields');
    return immutableFields;
}
function getFallbackImage(article) {
    if (!article.productCategory) {
        return null;
    }
    try {
        return productCategoryTree.getInheritedProperty(article.productCategory, 'icon');
    }
    catch (e) {
        const flaivyLogo = `https://assets.flaivy.com/623315650968a814f1d5739fe2548da4.png`;
        return flaivyLogo;
    }
}
const getDisplayUnit = article => {
    if (!article)
        return '';
    if (article.orderUnit === 'weight') {
        return 'kg';
    }
    if (article.orderUnit === 'package') {
        return 'frp';
    }
    return 'st';
};
function getViewModel(article) {
    const getDisplayWarehouseDeliveryStatus = () => [warehouseStatus(article).text, deliveryStatus(article)]
        .filter(x => !!x)
        .join(' - ')
        .trim();
    const getDisplayName = () => {
        if (_.get(article, 'brand.name') && _.get(article, 'shortName')) {
            return `${article.brand.name} ${article.shortName}`;
        }
        return `${article.shortName}`;
    };
    const prettyPrintVolume = () => {
        if (article.volume < 1) {
            return `${article.volume * 1000}ml`;
        }
        return `${article.volume}l`;
    };
    const getCaseUnitSizeAdjustment = () => {
        if (article.colliSize === 1 && article.caseUnitSize > 1) {
            return article.caseUnitSize;
        }
        return 1;
    };
    const prettyPrintWeight = () => {
        const caseUnitSizeAdjustment = getCaseUnitSizeAdjustment();
        if (article.weight / caseUnitSizeAdjustment >= 1000) {
            return `${_.round(article.weight / 1000 / caseUnitSizeAdjustment, 2)}kg`;
        }
        return `${_.round(article.weight / caseUnitSizeAdjustment)}g`;
    };
    const getOrderUnitSize = () => {
        if (article.orderUnit === 'single') {
            if (article.volume > 0) {
                return `1x${prettyPrintVolume()}`;
            }
            if (article.weight > 0) {
                return `1x${prettyPrintWeight()}`;
            }
            return '1 st';
        }
        if (article.orderUnit === 'package') {
            if (article.volume > 0) {
                if (article.caseUnitSize > 1 && article.colliSize === 1) {
                    return `${article.caseUnitSize}x${prettyPrintVolume()}`;
                }
                return `${article.colliSize}x${prettyPrintVolume()}`;
            }
            if (article.weight > 0) {
                if (article.caseUnitSize > 1 && article.colliSize === 1) {
                    return `${article.caseUnitSize}x${prettyPrintWeight()}`;
                }
                return `${article.colliSize}x${prettyPrintWeight()}`;
            }
            if (article.caseUnitSize > 1 && article.colliSize === 1) {
                return `${article.caseUnitSize}st`;
            }
        }
        if (article.orderUnit === 'weight') {
            return `${article.minWeightIncrement} kg`;
        }
        return `1x${article.colliSize}st`;
    };
    const getDisplayOrigin = () => (article.origin || '')
        .split('|')
        .map(x => _.capitalize(x))
        .join(' > ');
    const getDisplayPreparationFormat = () => {
        if (!article.preparationFormat)
            return '';
        return getTranslation('article.preparationFormats', article.preparationFormat);
    };
    const getDisplayContainer = () => {
        const containerLocalizedName = getTranslation('article.containers', article.container);
        if (article.orderUnit === orderUnits.weight) {
            return '';
        }
        const displayWeight = article.weight ? `${article.weight}g` : '';
        const displaySize = article.volume > 0
            ? literToAppropriateUnit(article.volume).replace(' ', '')
            : displayWeight;
        return article.container === 'Other'
            ? containerLocalizedName
            : `${containerLocalizedName} ${displaySize}`.trim();
    };
    return {
        ...article,
        displayOrderableUnit: getDisplayUnit(article),
        displayOrigin: getDisplayOrigin(),
        displayContainer: getDisplayContainer(),
        displayOrderUnitSize: getOrderUnitSize(),
        displayPreparationFormat: getDisplayPreparationFormat(),
        displayName: getDisplayName(),
        displayArticleNumber: article.onCopack
            ? article.supplierArticleNumber || article.articleNumber
            : article.articleNumber,
        displayWarehouseDeliveryStatus: getDisplayWarehouseDeliveryStatus(),
    };
}
function isAvailableAsSingleAndPackage(article) {
    return article.orderUnit === orderUnits.single && article.colliSize > 1;
}
function getRoundedQuantityInParcels(article, quantity) {
    const { colliSize = 1 } = article;
    return _.round(quantity / colliSize, 2);
}
// used in admin
function getCartOrderStateKey(article) {
    if (!article.myOrderFullfillmentCenter)
        return null;
    return `${article.myOrderFullfillmentCenter.id}-${article.currency}`;
}
function getTwoCharacterIsoCodeFromOrigin(article) {
    if (!article.origin)
        return article.origin;
    const split = article.origin.split('|');
    // eslint-disable-next-line consistent-return
    if (split.length > 1)
        return invertedOriginIsoCodesWithTwoCharacters[split[1]];
    return article.origin;
}
function getThreeCharacterIsoCodeFromOrigin(article) {
    if (!article.origin)
        return article.origin;
    const split = article.origin.split('|');
    if (split.length > 1)
        return invertedOriginIsoCodesWithThreeCharacters[split[1]];
    return article.origin;
}
module.exports = {
    getImmutablefields,
    getRoundedQuantityInParcels,
    getFallbackImage,
    calculateAlcoholTaxClass,
    calculateProductExciseCode,
    calculateBookKeepingAccounts,
    calculateVAT,
    calculateName,
    calculateRequiresAlcoholPermit,
    calculateNetWeightInKg,
    calculateGrossWeightInKg,
    getMinOrderableQuantityIncrement,
    getDisplayUnit, // todo remove this: still used in cartRow
    getViewModel,
    warehouseStatus,
    requiresEMCS,
    deliveryStatus,
    calculateDisposableSalesUnits,
    isFlaivyAssetImage,
    isAvailableAsSingleAndPackage,
    getCartOrderStateKey,
    getDisposableQuantity,
    DEFAULT_BATCH_NUMBER,
    isValidGTIN,
    getTwoCharacterIsoCodeFromOrigin,
    getThreeCharacterIsoCodeFromOrigin,
};
